import { dossierPackage } from "./quote-packages";

type Product = { id: number; name: string } & Record<string, unknown>;
export type TourChoice = { route: "esencial" | "fondo"; language: "es" | "en"; transport: boolean };

/** Reads route, guide language and transport from a Teotihuacán tour product; null for other products. */
export function tourChoice(product: Product | undefined): TourChoice | null {
  const pkg = dossierPackage(product);
  if (!product || !pkg || !["tour_esencial", "tour_full", "guided"].includes(pkg.package)) return null;
  const route = pkg.package === "tour_full" || /a fondo/i.test(product.name) ? "fondo" : "esencial";
  return { route, language: pkg.language, transport: pkg.package !== "guided" };
}

/** The product for a route, language and transport combination, if WooCommerce has it. */
export function tourProduct(products: Product[], choice: TourChoice) {
  return products.find((product) => {
    const current = tourChoice(product);
    return current && current.route === choice.route && current.language === choice.language && current.transport === choice.transport;
  });
}

/**
 * Teotihuacán tours as three simple choices instead of a long product list:
 * route (Esencial / A fondo), guide language and transport from CDMX.
 */
export function TourPicker({ products, productId, onChange }: { products: Product[]; productId: number | undefined; onChange: (id: number) => void }) {
  const selected = tourChoice(products.find((product) => product.id === productId));
  const current = selected ?? { route: "esencial", language: "es", transport: true };
  const others = products.filter((product) => !tourChoice(product));
  const pick = (next: Partial<TourChoice>) => {
    const choice = { ...current, ...next };
    const product = tourProduct(products, choice) ?? (next.transport === undefined ? undefined : tourProduct(products, { ...choice, route: current.route }));
    if (product) onChange(product.id);
  };
  const available = (next: Partial<TourChoice>) =>
    Boolean(tourProduct(products, { ...current, ...next }) ?? (next.transport === undefined ? undefined : tourProduct(products, { ...current, ...next, route: current.route })));
  const chip = (label: string, active: boolean, next: Partial<TourChoice>) => (
    <button type="button" className="funnel-chip" aria-pressed={active} disabled={!available(next)} onClick={() => pick(next)}>
      {label}
    </button>
  );
  return (
    <fieldset className="tour-picker span-10">
      <legend className="field__label">Tour en Teotihuacán</legend>
      <div className="tour-picker__row" role="group" aria-label="Recorrido">
        {chip("Esencial", !!selected && current.route === "esencial", { route: "esencial" })}
        {chip("A fondo", !!selected && current.route === "fondo", { route: "fondo" })}
      </div>
      <div className="tour-picker__row" role="group" aria-label="Idioma del guía">
        {chip("Español", !!selected && current.language === "es", { language: "es" })}
        {chip("Inglés", !!selected && current.language === "en", { language: "en" })}
      </div>
      {others.length > 0 && (
        <select className="tour-picker__other" aria-label="Otro tour" value={others.some((p) => p.id === productId) ? productId : ""} onChange={(e) => e.target.value && onChange(Number(e.target.value))}>
          <option value="">Otro tour…</option>
          {others.map((product) => <option key={product.id} value={product.id}>{product.name}</option>)}
        </select>
      )}
      <div className="tour-picker__row tour-picker__transport" role="group" aria-label="Transporte">
        {chip("🚐 Con transporte desde CDMX", !!selected && current.transport, { transport: true })}
        {chip("Sin transporte (llegan por su cuenta)", !!selected && !current.transport, { transport: false })}
      </div>
    </fieldset>
  );
}
